import {
  Button,
  Table,
  TableToolbar,
  TableWrapper,
} from 'melp-design/components';
import { Divider, Stack } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  AchievementsFilter,
  DateRangeFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
} from 'components/filters';
import { DownloadXlsAction } from 'components/actions';
import { usePointsLabel } from 'store/recognitions';
import {
  recognitionGiversFiltersSchema,
  useExportToXls,
  useRecognitionGivers,
} from 'store/recognition-givers';
import { useSearchParams } from 'utils/navigation';
import { APP } from 'config';
import { formatPercents } from 'utils/format';
import { useModalContext } from 'store/modal';
import { CompanyScopeInfoMessage } from 'containers/employees';
import { GiverRecipientsListModal } from './atoms';

export const RecognitionsGiversList = () => {
  const { t, i18n } = useTranslation();
  const pointsLabel = usePointsLabel();
  const { openModal } = useModalContext();
  const { searchParams: filters, navigate } = useSearchParams(
    recognitionGiversFiltersSchema,
  );

  const { data: givers, isLoading } = useRecognitionGivers(filters);
  const { mutate: exportToXls, isLoading: isExportingToXls } = useExportToXls();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <CompanyScopeInfoMessage />

          <Stack direction="row" justifyContent="end" gap={1}>
            <Filters
              value={filters}
              initialValues={recognitionGiversFiltersSchema.parse({})}
              onChange={(v) => navigate({ params: { ...v, page: 1 } })}
              keysToExtractFromTotalCalculation={['search']}
            >
              {({ value, initialValues, setFilter }) => (
                <>
                  <FilterButton
                    label={t('common.date')}
                    value={value.date}
                    initialValue={initialValues.date}
                    onChange={(v) => setFilter('date', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <DateRangeFilter
                        value={value}
                        onApply={applyFilter}
                        onClear={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('recognition.feedFiltersType')}
                    value={value.achievements}
                    initialValue={initialValues.achievements}
                    onChange={(v) => setFilter('achievements', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <AchievementsFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <OpenSearchFilter
                    value={filters.search}
                    onApplyFilter={(v) => setFilter('search', v)}
                    hint={t('recognition.recipientsListFiltersSearchHint')}
                  />
                </>
              )}
            </Filters>

            <Divider orientation="vertical" variant="middle" flexItem />

            <DownloadXlsAction
              listLength={givers?.total ?? 0}
              exportToExcel={() => exportToXls({ filters })}
              loading={isExportingToXls}
            />
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={givers?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'name',
            header: t('entity_type.employee'),
            cell: ({ row: { original: giver } }) => giver.fullName,
          },
          {
            id: 'givenRecognitionPoints',
            header: t('recognition.giversListTotalGiven', {
              recognitionCurrency: pointsLabel,
            }),
            cell: ({ row: { original: giver } }) =>
              giver.givenRecognitionPoints,
            meta: { sort: true, align: 'right' },
          },
          {
            id: 'participationPercentage',
            header: t('recognition.giversListParticipation'),
            cell: ({ row: { original: giver } }) =>
              formatPercents(
                i18n.language,
                giver.participationPercentage / 100,
              ),
            meta: { align: 'right' },
          },
          {
            id: 'givenRecognitionCount',
            header: t('recognition.giversListGivenCount'),
            cell: ({ row: { original: giver } }) => giver.givenRecognitionCount,
            meta: { align: 'right' },
          },
          {
            id: 'recipientsDetails',
            header: t('common.recipients'),
            cell: ({ row: { original: giver } }) => (
              <Button
                label={t('recognition.giversListRecipientsDetailsLink')}
                size="sm"
                variant="neutral-outline"
                onClick={() => {
                  openModal({
                    component: GiverRecipientsListModal,
                    props: {
                      employeeId: giver.id,
                      date: filters.date,
                      achievements: filters.achievements,
                    },
                    size: 'lg',
                  });
                }}
              />
            ),
          },
          {
            id: 'lastGivenRecognitionAt',
            header: t('recognition.giversListLastActivity'),
            cell: ({ row: { original: giver } }) =>
              giver.lastGivenRecognitionAt
                ? moment(giver.lastGivenRecognitionAt).format('l')
                : null,
          },
        ]}
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
        pagination={
          givers && {
            count: givers.total,
            page: givers.page,
            pageSize: givers.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
      />
    </TableWrapper>
  );
};
